import React from "react";
import { RefreshCw, TriangleAlert, Wallet } from "lucide-react";
import Button from "../common/Button";
import Card from "../common/Card";
import Notice from "../common/Notice";
import SupernovaCard from "./SupernovaCard";
import { SUPERNOVA_TOKENS } from "./supernovaContracts";
import { isSupernovaBuyEnabled } from "../../config/supernovaFlags";

/**
 * The Supernova tab's non-data states: connect, empty, loading, error.
 *
 * Per docs/DESIGN_SYSTEM.md §7 — loading is skeleton blocks in arc-paper-2 (not
 * a spinner), empty is one quiet line plus a secondary action, and an error is
 * arc-neg on arc-neg-soft with member-safe copy, never a stack trace.
 *
 * Every one of these is a state where we have NO numbers, and each says so in
 * words instead of rendering a zero.
 */

/* ── Loading ────────────────────────────────────────────────────────────── */

/** One skeleton block. `radius` is a Tailwind radius utility, not a className,
    so it can't collide with the default in the class list. */
export const Skeleton = ({ className = "", radius = "rounded-arc-sm" }) => (
  <span
    className={`block bg-arc-paper-2 animate-pulse ${radius} ${className}`}
    aria-hidden="true"
  />
);

/** Screen-reader-only status line, so a skeleton isn't silence to a reader. */
export const LoadingAnnouncement = ({ children }) => (
  <span role="status" className="sr-only">
    {children}
  </span>
);

/** Collection placeholder — one card-shaped block per token in the collection,
    laid out on the same grid so the real cards don't shift in on arrival. */
export const CollectionSkeleton = () => (
  <div className="sn-grid">
    <LoadingAnnouncement>Reading your Supernova collection from Arbitrum…</LoadingAnnouncement>
    {SUPERNOVA_TOKENS.map((token) => (
      <div key={token.id} className="sn-card-inner ring-1 ring-arc-line">
        <Skeleton radius="rounded-none" className="w-full aspect-[1/1.12]" />
        <div className="flex items-center justify-between gap-3 px-5 py-4">
          <Skeleton className="h-[12px] w-[52%] max-w-[150px]" />
          <Skeleton className="h-[34px] w-[76px] flex-none" />
        </div>
      </div>
    ))}
  </div>
);

/* ── No wallet connected ────────────────────────────────────────────────── */

export const ConnectWalletCard = ({ onConnect, connecting, hasInjected, error }) => (
  <Card hover={false} className="text-center">
    <Wallet size={22} className="mx-auto text-arc-accent-ink mb-2" aria-hidden="true" />
    <h3 className="text-h2 text-arc-ink">Connect your wallet</h3>
    <p className="text-body text-arc-ink-2 mt-2 mb-4 max-w-md mx-auto">
      Your Supernovas and stakes live on Arbitrum, in your own wallet. Connect it
      and this tab reads the chain — read-only. ARC Educational never holds your
      keys, your NFTs, or your funds, and nothing here can sign for you.
    </p>
    <Button onClick={onConnect} disabled={connecting || !hasInjected}>
      <Wallet size={15} aria-hidden="true" />
      {connecting ? "Connecting…" : "Connect wallet"}
    </Button>
    {!hasInjected && (
      <p className="text-caption text-arc-ink-3 mt-3">
        No browser wallet detected. Install MetaMask, Rabby, or another EVM
        wallet and reload this page.
      </p>
    )}
    {error && <p className="text-caption text-arc-neg mt-3">{error}</p>}
  </Card>
);

/* ── Walletless showcase ────────────────────────────────────────────────── */

const SHOWCASE_STAKE_REASON = "Connect your wallet to stake.";

/**
 * The four collection types, shown alongside the connect card to a member with
 * no wallet connected.
 *
 * Same row as the real list, minus every figure: with no address we have read
 * nothing, so each row says "Connect a wallet to see yours" where the owned and
 * staked counts go. That is the same rule as everywhere else on this tab — no
 * number we haven't read, and never a zero standing in for one.
 *
 * The Stake buttons are disabled and say why. There is nothing to stake from
 * without a wallet, and nothing to sign with either.
 *
 * This exists because "what is in this collection" is a fair question for
 * someone who hasn't connected, and the connect card alone can't answer it.
 */
export const CollectionShowcase = () => (
  <div className="sn-grid" data-testid="supernova-collection-showcase">
    {SUPERNOVA_TOKENS.map((token) => (
      <SupernovaCard
        key={token.id}
        tokenId={token.id}
        name={token.name}
        footer={
          <p className="text-[12px] text-arc-ink-4 m-0">Connect a wallet to see yours</p>
        }
        action={
          <button type="button" className="sn-ghost" disabled title={SHOWCASE_STAKE_REASON}>
            Stake
            <span className="sr-only"> — {SHOWCASE_STAKE_REASON}</span>
          </button>
        }
      />
    ))}
  </div>
);

/* ── Wrong network ──────────────────────────────────────────────────────── */

export const WrongNetworkNotice = ({ onSwitch, switching }) => (
  <Notice tone="warn">
    <span className="font-semibold text-arc-warn">Wrong network.</span> Supernova
    reads Arbitrum One. We&apos;ve paused the read rather than show you balances
    from another chain.{" "}
    <button
      type="button"
      onClick={onSwitch}
      disabled={switching}
      className="underline font-semibold text-arc-warn disabled:opacity-50"
    >
      {switching ? "Switching…" : "Switch to Arbitrum"}
    </button>
  </Notice>
);

/* ── Chain error ────────────────────────────────────────────────────────── */

/**
 * `message` is the member-safe string off a SupernovaChainError. The underlying
 * error stays in the console — never on screen.
 */
export const ChainErrorCard = ({ title, message, onRetry, retrying }) => (
  // arc-neg on arc-neg-soft per DESIGN_SYSTEM §7. The hairline stays arc-line:
  // Tailwind can't apply an opacity modifier to a var()-based color, and a
  // solid arc-neg ring shouts louder than a read failure warrants.
  <div className="flex items-start gap-3 rounded-arc-card bg-arc-neg-soft ring-1 ring-arc-line p-[22px]">
    <TriangleAlert size={18} className="text-arc-neg flex-shrink-0 mt-0.5" aria-hidden="true" />
    <div className="flex-1 min-w-0">
      <p className="font-semibold text-arc-neg">{title}</p>
      <p className="text-body text-arc-ink-2 mt-1">{message}</p>
      <p className="text-caption text-arc-ink-3 mt-2">
        Nothing on-chain changed — this is a reading problem, not a change to
        what you own or have staked.
      </p>
      {onRetry && (
        <Button variant="secondary" className="mt-3" onClick={onRetry} disabled={retrying}>
          <RefreshCw size={14} aria-hidden="true" />
          {retrying ? "Retrying…" : "Try again"}
        </Button>
      )}
    </div>
  </div>
);

/* ── Empty collection ───────────────────────────────────────────────────── */

/**
 * The chain answered, and the answer was "none". Distinct from every state
 * above, and the only one where a zero is the truth.
 *
 * `stakeStateKnown` matters: with the stake read unavailable we can say the
 * WALLET is empty, because balanceOfBatch told us so — but we cannot also claim
 * nothing is staked, because we haven't read that.
 */
export const EmptyCollectionCard = ({ stakeStateKnown = false }) => (
  <Card hover={false} className="text-center">
    <h3 className="text-h2 text-arc-ink">No Supernovas in this wallet</h3>
    <p className="text-body text-arc-ink-2 mt-2 max-w-md mx-auto">
      This wallet holds none of the {SUPERNOVA_TOKENS.length} Supernova types
      {stakeStateKnown
        ? ", and has nothing staked."
        : ". We can't see your stakes yet — that read isn't connected, so this doesn't tell you whether you have anything staked."}{" "}
      If you hold them in a different wallet, connect that one instead.
    </p>
    {!isSupernovaBuyEnabled() && (
      <p className="text-caption text-arc-ink-3 mt-3">
        {/* TODO(backend, supervised): the acquire path is seamed off behind
            SUPERNOVA_BUY_ENABLED until a buy/mint flow exists. No venue, no
            price, no purchase surface — so there is nothing to link to here. */}
        Acquiring Supernovas from the app isn&apos;t available yet.
      </p>
    )}
  </Card>
);
